import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import RiderLayout from "@/components/RiderLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Wallet, IndianRupee, TrendingUp, Clock, 
  CheckCircle2, AlertCircle, RefreshCw
} from "lucide-react";

const RiderCOD = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [codSummary, setCodSummary] = useState({
    todayCollected: 0,
    todayPending: 0,
    totalOutstanding: 0,
    lastRemittance: null,
  });
  const [pendingDeliveries, setPendingDeliveries] = useState([]);

  useEffect(() => {
    fetchCODData();
  }, []);

  const fetchCODData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/rider/cod/summary`);
      setCodSummary(res.data.summary || {
        todayCollected: 0,
        todayPending: 0,
        totalOutstanding: 0,
        lastRemittance: null,
      });
      setPendingDeliveries(res.data.pending || []);
    } catch (error) {
      console.error("Error fetching COD data:", error);
      toast.error("Failed to load COD data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <RiderLayout user={user} onLogout={onLogout}>
      <div className="space-y-4" data-testid="rider-cod-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-slate-800">COD Summary</h1>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchCODData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700">Collected Today</span>
              </div>
              <p className="text-2xl font-bold text-green-800">
                {formatCurrency(codSummary.todayCollected)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-amber-600" />
                <span className="text-sm text-amber-700">Pending Today</span>
              </div>
              <p className="text-2xl font-bold text-amber-800">
                {formatCurrency(codSummary.todayPending)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Outstanding Balance */}
        <Card className={codSummary.totalOutstanding > 0 ? 'bg-red-50 border-red-200' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Outstanding</p>
                <p className={`text-3xl font-bold ${
                  codSummary.totalOutstanding > 0 ? 'text-red-700' : 'text-slate-800'
                }`}>
                  {formatCurrency(codSummary.totalOutstanding)}
                </p>
                {codSummary.totalOutstanding > 0 && (
                  <p className="text-xs text-red-600 mt-1">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    Please remit this amount at the hub
                  </p>
                )}
              </div>
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
                <Wallet className="w-7 h-7 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Last Remittance */}
        {codSummary.lastRemittance && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800">Last Remittance</p>
                  <p className="text-xs text-slate-500">
                    {formatCurrency(codSummary.lastRemittance.amount)} on{' '}
                    {new Date(codSummary.lastRemittance.date).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending COD Deliveries */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              <span>Pending COD Deliveries</span>
              <Badge variant="outline">{pendingDeliveries.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : pendingDeliveries.length === 0 ? (
              <div className="text-center py-8">
                <IndianRupee className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No pending COD deliveries</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingDeliveries.map((delivery, index) => (
                  <div
                    key={delivery._id || index}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">
                        {delivery.recipientName || 'Customer'}
                      </p>
                      <p className="text-xs text-slate-500">
                        {delivery.shipmentId || delivery.referenceId}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-amber-700">
                        {formatCurrency(delivery.codAmount)}
                      </p>
                      <Badge className="bg-amber-100 text-amber-700 text-xs">
                        COD
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </RiderLayout>
  );
};

export default RiderCOD;
